import React, { useMemo, useState, isValidElement } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import Icon from "./Icon";
import Text from "./Text";
import { cn } from "@/lib/utils";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";

interface MultiSelectOption {
  id: string;
  label: string;
}

interface MultiSelectProps {
  title?: string;
  applyLabel?: string;
  className?: string;
  options: MultiSelectOption[];
  initialSelected?: string[];
  onChange: (selected: string[]) => void;
  trigger: string | React.ReactNode;
  screen_name: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  title,
  applyLabel,
  className,
  options,
  initialSelected = [],
  onChange,
  trigger,
  screen_name,
}) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      options.map((option) => [option.id, initialSelected.includes(option.id)])
    )
  );
  const [isOpen, setIsOpen] = useState(false);
  const { sendEvent } = useAnalyticsManager();

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase())
      ),
    [options, search]
  );

  const handleToggle = (id: string, value: boolean) => {
    setSelected((prev) => ({ ...prev, [id]: value }));
    const option = options.find((option) => option.id === id);
    if (value) {
      sendEvent("edit_column_selected", {
        column_name: option?.label || "",
        selection_count: Object.values(selected).filter(Boolean).length + 1,
        screen_name,
      });
    } else {
      sendEvent("edit_column_deselected", {
        column_name: option?.label || "",
        selection_count: Object.values(selected).filter(Boolean).length + 1,
        screen_name,
      });
    }
  };

  const handleDeselectAll = () => {
    const currentSelectionCount =
      Object.values(selected).filter(Boolean).length;
    setSelected(
      Object.fromEntries(options.map((option) => [option.id, false]))
    );

    sendEvent("edit_column_clear_clicked", {
      selection_count: currentSelectionCount,
      screen_name,
    });
  };

  const handleClear = handleDeselectAll;

  const handleApply = () => {
    const selectedIds = Object.entries(selected)
      .filter(([, value]) => value)
      .map(([key]) => key);

    sendEvent("edit_column_apply_clicked", {
      final_columns_selected: selectedIds.join(","),
      screen_name,
    });
    onChange(selectedIds);
    setIsOpen(false);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const isApplyDisabled = selectedCount === 0;
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        {isValidElement(trigger) ? (
          trigger
        ) : (
          <Button variant="outline" className="ml-auto">
            {trigger} <Icon name="chevronDown" className="ml-2 h-4 w-4" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[320px] rounded-none p-3">
        <div className={cn("flex h-[400px] flex-col", className)}>
          {title ? (
            <>
              <div className="flex w-full border-b border-brand-border px-2">
                <Text className="pb-3 text-[16px]">{title}</Text>
              </div>
            </>
          ) : (
            <></>
          )}
          <div className="flex-grow overflow-y-auto py-3">
            {filteredOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.id}
                className="capitalize"
                checked={selected[option.id] ?? false}
                onCheckedChange={(value: boolean) => {
                  handleToggle(option.id, value);
                }}
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                <Text className="ml-2 capitalize">{option.label}</Text>
              </DropdownMenuCheckboxItem>
            ))}
          </div>
          <div className="flex w-full items-center border-t border-brand-border py-3">
            <Button
              variant="ghost"
              className="text-brand-text"
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              variant="outline"
              className="ml-auto text-brand-accent"
              onClick={handleApply}
              disabled={isApplyDisabled}
            >
              {applyLabel ? applyLabel : "Apply"}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MultiSelect;
